// The organization roster (`06` §3.3, `SC-F103`).
//
// Two invariants the UI must render honestly rather than discover through a
// 409: an organization always has at least one owner, and a maintainer may
// manage viewers, members and maintainers but never touch an owner row. The
// last-owner check runs server-side in one transaction — two concurrent
// removals on a two-owner organization would otherwise both observe
// `owners == 2` and leave zero — so the client-side guards below are a courtesy
// that prevents an obviously-doomed request, never the enforcement.

import { del, getJSON, newIdempotencyKey, sendJSON, type Page } from "./http.js";
import { atLeast, type Duty, type Role } from "./orgs.js";

/** Member is one row of the roster. `label` is an attribution snapshot suitable
 * for display even after the account is removed. */
export interface Member {
  /** The membership record id. */
  id: string;
  /** The account record id — what the role and remove routes address. */
  account: string;
  email: string;
  name: string;
  role: Role;
  /**
   * The duty classes subtracted from this membership (`SC-F914`), sorted and
   * never null.
   *
   * It is on the roster because a console that renders a member's authority has
   * to render the narrowing with it: a maintainer excluded from `operate` reads
   * as an ordinary maintainer, and the reason their controls are missing is
   * otherwise invisible.
   */
  excluded_duties: Duty[];
  created: string;
}

/** listMembers returns the organization's roster (viewer and above). */
export function listMembers(orgId: string): Promise<Page<Member>> {
  return getJSON<Page<Member>>(`/v1/orgs/${encodeURIComponent(orgId)}/members`);
}

/**
 * addMember adds an **existing** account to the organization. Inviting someone
 * who has no account yet is a different operation with different privacy
 * properties — see `api/invites.ts`.
 */
export function addMember(
  orgId: string,
  input: { email: string; role: Role },
): Promise<Member> {
  return sendJSON<Member>(`/v1/orgs/${encodeURIComponent(orgId)}/members`, "POST", input, {
    idempotencyKey: newIdempotencyKey(),
  });
}

/**
 * setMemberRole changes a member's role (`SC-F103`). Promoting someone to owner
 * is the one membership change that grants authority the actor cannot later
 * revoke, so it is step-up gated at MVP (`change_owner`, `01` §5.4) — pass the
 * grant as `verification`.
 */
export function setMemberRole(
  orgId: string,
  accountId: string,
  role: Role,
  opts: { verification?: string } = {},
): Promise<Member> {
  return sendJSON<Member>(
    `/v1/orgs/${encodeURIComponent(orgId)}/members/${encodeURIComponent(accountId)}`,
    "PATCH",
    { role },
    opts,
  );
}

/**
 * removeMember removes a member. The server refuses to remove the last owner
 * (`409 conflict`); an organization with no owner is unrecoverable without
 * staff intervention.
 */
export function removeMember(orgId: string, accountId: string): Promise<void> {
  return del(
    `/v1/orgs/${encodeURIComponent(orgId)}/members/${encodeURIComponent(accountId)}`,
  );
}

/**
 * canManage mirrors the server's `access.CanManageMembers` so a roster can
 * disable the controls it knows will be refused: managing a viewer, member or
 * maintainer needs maintainer or above; touching an **owner** row needs owner.
 * It is a UI courtesy, not the enforcement — the route checks it again.
 */
export function canManage(actor: Role | undefined, subject: Role): boolean {
  if (!atLeast(actor, "maintainer")) return false;
  if (subject === "owner") return actor === "owner";
  return true;
}
